import { CalendarDays, Clock, DollarSign, Pencil, Trash2, StickyNote } from "lucide-react";
import { fmtHours, fmtMoney } from "@/lib/calc";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

function formatDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return d;
  }
}

export default function EntryList({ entries, currency, onEdit, onDelete }) {
  if (entries.length === 0) {
    return (
      <div data-testid="entries-empty" className="bg-[#181a22] border border-dashed border-[#2b2f3d] rounded-xl p-12 text-center">
        <Clock className="w-8 h-8 text-[#62697e] mx-auto" />
        <p className="mt-3 text-[#9fa6b9]">No overtime logged yet.</p>
        <p className="mt-1 font-mono-data text-xs text-[#62697e]">Use the punch clock to add your first entry.</p>
      </div>
    );
  }

  return (
    <div data-testid="entries-list-container" className="space-y-3">
      {entries.map((e) => (
        <div
          key={e.id}
          data-testid="entry-row-item"
          className="group bg-[#181a22] border border-[#2b2f3d] rounded-xl p-4 hover:border-[#3a3f52] transition-colors"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <div className="flex flex-col items-center justify-center w-14 h-14 bg-[#14161c] border border-[#2b2f3d] rounded-lg shrink-0">
                <CalendarDays className="w-3.5 h-3.5 text-amber-500" />
                <span className="font-mono-data text-[10px] text-[#9fa6b9] mt-0.5 text-center leading-tight px-1">{formatDate(e.date)}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-[#f3f4f8] uppercase tracking-tight">{e.project}</span>
                  <span className="rounded-full px-2 py-0.5 font-mono-data text-[10px] tracking-wide bg-[#20232e] text-[#9fa6b9] border border-[#2b2f3d]">
                    {e.start_time}–{e.end_time}
                  </span>
                  <span className="rounded-full px-2 py-0.5 font-mono-data text-[10px] tracking-wide bg-[#20232e] text-[#f97316] border border-[#2b2f3d]">
                    {Number(e.multiplier).toFixed(1)}x
                  </span>
                </div>
                {e.notes && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[#62697e] truncate">
                    <StickyNote className="w-3 h-3 shrink-0" /> <span className="truncate">{e.notes}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end text-amber-500 font-mono-data font-bold">
                  <Clock className="w-3.5 h-3.5" /> {fmtHours(e.duration_hours)}
                </div>
                <div className="flex items-center gap-1 justify-end text-emerald-500 font-mono-data font-bold mt-0.5">
                  <DollarSign className="w-3.5 h-3.5" /> {fmtMoney(e.earnings, currency)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid="entry-edit-button" onClick={() => onEdit(e)} className="p-2 rounded-lg text-[#9fa6b9] hover:text-amber-500 hover:bg-[#20232e] transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="entry-delete-button" className="p-2 rounded-lg text-[#9fa6b9] hover:text-red-400 hover:bg-[#20232e] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#181a22] border-[#2b2f3d] text-[#f3f4f8]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display uppercase tracking-tight">Delete this overtime entry?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#9fa6b9]">
                        This removes the {fmtHours(e.duration_hours)} logged on {formatDate(e.date)}. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#20232e] border-[#2b2f3d] text-[#f3f4f8] hover:bg-[#2b2f3d] hover:text-[#f3f4f8]">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="entry-delete-confirm-button" onClick={() => onDelete(e.id)} className="bg-red-500 hover:bg-red-400 text-white">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
